export type ExecutionKind = 'objective' | 'strategy' | 'project' | 'task';
export type OwnerHealth = 'on_track' | 'at_risk' | 'off_track';
export type ProgressMode = 'calculated' | 'manual';
export type PrimaryMeasure =
  | {
      kind: 'numeric';
      label: string;
      unit: string;
      baseline: number;
      target: number;
      baselineDate?: string | null;
    }
  | { kind: 'milestone'; label: string };

export interface ExecutionPerson {
  id: string;
  name: string;
  email?: string;
  photo?: string | null;
  isPrimary?: boolean;
}
export interface ExecutionPosition {
  id: string;
  title: string;
  holders: ExecutionPerson[];
}
export interface ExecutionOwner {
  userId: string | null;
  name: string | null;
  positionId: string | null;
  positionTitle: string | null;
  holders: ExecutionPerson[];
  vacant: boolean;
}
export interface OutcomeSummary {
  currentValue: number | null;
  milestoneAchieved: boolean | null;
  latestReadingAt: string | null;
  ownerHealth: OwnerHealth | null;
  latestHealthAt: string | null;
  progressPercent: number | null;
  pace: 'ahead' | 'on_pace' | 'behind' | 'unavailable' | 'pending' | 'overdue' | 'achieved';
  expectedValue: number | null;
  paceGap: number | null;
  stale: boolean;
}
export interface ExecutionNode {
  id: string;
  type: ExecutionKind;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  owner: ExecutionOwner;
  manager?: ExecutionOwner | null;
  dueDate: string | null;
  parentId: string | null;
  objectiveId: string | null;
  progressMode: ProgressMode;
  storedProgress: number;
  deliveryCompletion: number;
  estimatedHours: number;
  attentionReasons: string[];
  children: ExecutionNode[];
  referenceNumber?: string | null;
  aiGenerated?: boolean;
  blockedBy?: string[];
}
export interface ExecutionObjective extends ExecutionNode {
  type: 'objective';
  measureLabel: string | null;
  primaryMeasure: PrimaryMeasure | null;
  measureRevision: number;
  reviewIntervalDays: number;
  outcome: OutcomeSummary;
}
export interface ExecutionSnapshot {
  company: { id: string; name: string; logo?: string | null };
  currentUserId: string;
  canEdit: boolean;
  objectives: ExecutionObjective[];
  unlinked: ExecutionNode[];
  people: ExecutionPerson[];
  positions: ExecutionPosition[];
  generatedAt: string;
}
export interface ObjectiveCheckIn {
  id: string;
  objectiveId: string;
  measureRevision: number;
  measureSnapshot: PrimaryMeasure | null;
  deadlineSnapshot: string | null;
  observedAt: string;
  value: number | null;
  milestoneAchieved: boolean | null;
  ownerHealth: OwnerHealth | null;
  note: string | null;
  authorId: string;
  authorName: string;
  createdAt: string;
}
export interface CheckInInput {
  expectedRevision: number;
  observedAt: string;
  value?: number | null;
  milestoneAchieved?: boolean | null;
  ownerHealth?: OwnerHealth | null;
  note?: string;
}
export interface ExecutionParent {
  type: ExecutionKind;
  id: string;
}
export interface NodeInput {
  title: string;
  description?: string | null;
  priority?: string;
  status?: string;
  assignedTo?: string | null;
  assignedPositionId?: string | null;
  dueDate?: string | null;
  estimatedHours?: number | null;
  progressMode?: ProgressMode;
  progress?: number;
}
export interface ExecutionRecommendation {
  id: string;
  action:
    | 'record_measure'
    | 'clarify_measure'
    | 'assign_owner'
    | 'unblock_work'
    | 'review_work'
    | 'draft_children';
  entityRef: ExecutionParent;
  title: string;
  rationale: string;
  expectedContribution: string;
  suggestedOwnerId?: string | null;
  suggestedPositionId?: string | null;
}
export interface ExecutionReview {
  contextHash: string;
  generatedAt: string;
  summary: string;
  recommendations: ExecutionRecommendation[];
  risks: string[];
  missingEvidence: string[];
}
export interface ExecutionDraftItem {
  id: string;
  title: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  parent: ExecutionParent;
  expectedContribution: string;
  definitionOfDone: string;
  assignedTo: string | null;
  assignedPositionId: string | null;
  dueDate: string | null;
  estimatedHours?: number | null;
  blockedBy: string[];
}
export interface ExecutionDraft {
  draftId: string;
  objectiveId: string;
  contextHash: string;
  kind: 'strategy' | 'project' | 'task';
  parent: ExecutionParent;
  rationale: string;
  items: ExecutionDraftItem[];
  warnings: string[];
}
export interface ExecutionApplyInput {
  operationId: string;
  objectiveId: string;
  contextHash: string;
  kind: ExecutionDraft['kind'];
  parent: ExecutionParent;
  items: ExecutionDraftItem[];
}
export interface ExecutionApplyResult {
  created: Array<{ id: string; type: ExecutionKind; title: string }>;
  referenceMapping: Record<string, string>;
}

export type WorkFilter = 'all' | 'my_work' | 'needs_attention' | 'starred';
export interface ExecutionFilters {
  query: string;
  work: WorkFilter;
  status?: string;
  priority?: string;
  ownerId?: string;
}
export function flattenExecution(nodes: ExecutionNode[]): ExecutionNode[] {
  return nodes.flatMap((node) => [node, ...flattenExecution(node.children)]);
}
export function isAssignedTo(node: ExecutionNode, userId: string): boolean {
  return node.owner.userId === userId || node.owner.holders.some((holder) => holder.id === userId);
}
export function filterExecutionTree(
  nodes: ExecutionNode[],
  filters: ExecutionFilters,
  userId: string,
  starred: Set<string>
): ExecutionNode[] {
  const query = filters.query.trim().toLocaleLowerCase();
  const matches = (node: ExecutionNode) =>
    (!query ||
      [
        node.title,
        node.description,
        node.referenceNumber,
        node.owner.name,
        node.owner.positionTitle,
      ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase()
        .includes(query)) &&
    (!filters.status || node.status === filters.status) &&
    (!filters.priority || node.priority === filters.priority) &&
    (!filters.ownerId || isAssignedTo(node, filters.ownerId)) &&
    (filters.work === 'all' ||
      (filters.work === 'my_work' && isAssignedTo(node, userId)) ||
      (filters.work === 'needs_attention' && node.attentionReasons.length > 0) ||
      (filters.work === 'starred' && starred.has(node.id)));
  return nodes.flatMap((node) => {
    const children = filterExecutionTree(node.children, filters, userId, starred);
    if (!matches(node) && children.length === 0) return [];
    // Searching a parent reveals its plan. Assignment/status filters still apply to descendants.
    const searchOnly =
      Boolean(query) &&
      filters.work === 'all' &&
      !filters.status &&
      !filters.priority &&
      !filters.ownerId;
    return [{ ...node, children: matches(node) && searchOnly ? node.children : children }];
  });
}
export function ownerLabel(owner: ExecutionOwner): string {
  if (owner.name && owner.positionTitle) return `${owner.name} · ${owner.positionTitle}`;
  if (owner.name) return owner.name;
  if (owner.positionTitle) {
    const names = owner.holders.map((holder) => holder.name).join(', ');
    return `${owner.positionTitle} · ${names || 'Position vacant'}`;
  }
  return 'Unassigned';
}
export function sortObjectives(objectives: ExecutionObjective[]): ExecutionObjective[] {
  const priority: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
  const severity = (objective: ExecutionObjective) =>
    objective.outcome.ownerHealth === 'off_track'
      ? 4
      : objective.outcome.ownerHealth === 'at_risk'
        ? 3
        : objective.outcome.pace === 'behind' || objective.outcome.pace === 'overdue'
          ? 2
          : objective.attentionReasons.length
            ? 1
            : 0;
  return [...objectives].sort(
    (a, b) =>
      severity(b) - severity(a) ||
      (priority[b.priority] ?? 0) - (priority[a.priority] ?? 0) ||
      (a.dueDate ? Date.parse(a.dueDate) : Infinity) -
        (b.dueDate ? Date.parse(b.dueDate) : Infinity) ||
      a.title.localeCompare(b.title)
  );
}
